<script setup lang="ts">
import type { SliderItem } from '@/types/appTypes'

import { Hooper, Slide, Navigation, Pagination } from '@wattanx/hooper-vue3'
import '@wattanx/hooper-vue3/css'

type Props = { items: SliderItem[] }

const props = withDefaults(defineProps<Props>(), {})

const app = useNuxtApp()

const hooperMain = {
  itemsToShow: 3,
  centerMode: true,
  infiniteScroll: true,
  wheelControl: false,
  autoPlay: true,
  playSpeed: 5000,
  transition: 600,
  breakpoints: {
    0: { itemsToShow: 1 },
    751: { itemsToShow: 3 },
  },
}

const mainSlider = ref(null)

const onClickMainPrev = () => {
  mainSlider.value.restartTimer()
  mainSlider.value.slidePrev()
}

const onClickMainNext = () => {
  mainSlider.value.restartTimer()
  mainSlider.value.slideNext()
}
</script>

<template>
  <div class="c-topSlider">
    <hooper
      key="main"
      ref="mainSlider"
      v-bind:settings="hooperMain"
      auto-play
      v-bind:play-speed="5000"
      v-bind:transition="600"
    >
      <slide
        v-for="(item, index) in items"
        v-bind:key="index"
        v-bind:index="index"
      >
        <div class="c-topSlider_item">
          <CommonImage
            v-bind:src="item.image.url"
            class="c-topSlider_item_image"
          />
          <div class="c-topSlider_item_cover" />
          <div
            class="c-topSlider_item_textGroup"
          >
            <nuxt-link
              v-bind:to="item.link"
            >
              <div
                class="c-topSlider_item_textGroup_plate"
                v-bind:class="{
                  'is-imReport': item.type === 'im_report',
                  'is-special': item.type === 'special',
                  'is-news': item.type === 'news',
                }"
              />
              <div class="c-topSlider_item_textGroup_type">
                <template
                  v-if="item.type === 'im_report'"
                >
                  IMレポート
                </template>
                <template
                  v-else-if="item.type === 'special'"
                >
                  特集
                </template>
                <template
                  v-else-if="item.type === 'news'"
                >
                  ニュース
                </template>
                <template
                  v-else
                >
                  読者レポート
                </template>
              </div>
              <div class="c-topSlider_item_textGroup_title">
                {{ item.title }}
              </div>
              <div
                class="c-topSlider_item_textGroup_body"
                v-html="app.$trimEnter(item.body)"
              />
            </nuxt-link>
          </div>
        </div>
      </slide>
    </hooper>
    <div class="c-topSlider_buttonGroup">
      <v-btn
        class="c-topSlider_buttonGroup_button"
        tile
        v-on:click.prevent="onClickMainPrev"
      >
        <v-icon class="c-topSlider_buttonGroup_button_icon">
          mdi-chevron-left
        </v-icon>
      </v-btn>
      <v-btn
        class="c-topSlider_buttonGroup_button"
        tile
        v-on:click.prevent="onClickMainNext"
      >
        <v-icon class="c-topSlider_buttonGroup_button_icon">
          mdi-chevron-right
        </v-icon>
      </v-btn>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/foundation/mixin/responsive";

.c-topSlider {
  position: absolute;
  top: 0;
  left: 50%;
  width: 3420px;
  height: 600px;
  margin-left: -1710px;

  @include responsive.mq_down(sp) {
    left: 0;
    width: 100%;
    height: 430px;
    margin-left: 0;
  }

  &_buttonGroup {
    display: flex;
    position: absolute;
    z-index: 4;
    right: 1160px;
    bottom: 0;

    &_button {
      width: 64px;
      height: 40px;
      background-color: rgb(0, 0, 0, 0.2);
      color: var(--g_color_button_text_white);

      &_icon {
        font-size: 30px;
      }
    }
  }

  :deep(.hooper) {
    height: auto;
  }

  :deep(.hooper-slide) {
    &.is-current {
      .c-topSlider_item_textGroup_plate {
        animation:
          slidePlate 0.5s 0.7s cubic-bezier(0.28, 0.7, 0.32, 1.02)
          both;
      }

      .c-topSlider_item_textGroup_type {
        animation: slideText 0.5s 0.9s ease-out both;
      }

      .c-topSlider_item_textGroup_title {
        animation: slideText 0.5s 1s ease-out both;
      }

      .c-topSlider_item_textGroup_body {
        animation: slideText 0.5s 1.1s ease-out both;
      }

      .c-topSlider_item_cover {
        animation: fadeOutCover 0.5s both;
      }
    }
  }

  :deep(.hooper-pagination) {
    padding: 12px 0;

    @include responsive.mq_down(sp) {
      padding: 8px 0;
    }
  }

  :deep(.hooper-indicators) {
    display: none;
    padding: 0 !important;

    @include responsive.mq_down(sp) {
      display: flex;
    }
  }

  :deep(.hooper-indicator) {
    width: 8px;
    height: 8px;
    margin: 0 4px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background-color: #fff;
    cursor: pointer;

    &:hover,
    &.is-active {
      background-color: var(--color_primary);
    }
  }

  &_item {
    position: relative;
    width: var(--g_min_screen_width_pc);
    height: 600px;
    padding: 0 20px;

    @include responsive.mq_down(sp) {
      width: 100%;
      height: 430px;
      padding: 0;
    }

    &_image {
      width: 100%;
      height: 100%;

      :deep(img) {
        height: 100%;
        object-fit: cover !important;
      }
    }

    &_imageBiggerHeight {
      width: 100%;
      height: 100%;
    }

    &_buttonGroup {
      display: flex;
      position: absolute;
      z-index: 10;
      right: 1160px;
      bottom: 0;

      &_button {
        width: 64px;
        height: 40px;
        background-color: rgb(0, 0, 0, 0.1);
        color: var(--g_color_button_text_white);

        &_icon {
          font-size: 30px;
        }
      }
    }

    &_textGroup {
      position: absolute;
      bottom: 40px;
      left: 20px;
      width: 730px;
      min-height: 260px;
      overflow: hidden;
      color: #fff;
      font-family: "Noto Sans JP", sans-serif;
      font-size: 14px;

      @include responsive.mq_down(sp) {
        right: 25px;
        bottom: 30px;
        left: 0;
        width: auto;
        min-height: auto;
      }

      & > a {
        display: block;
        min-height: 260px;
        padding: 25px 30px 35px 53px;
        transition: background 0.4s;
        color: #fff;

        &:hover {
          transition: background 0.1s;
          background-color: rgb(255, 255, 255, 0.5);
          text-decoration: none;
        }

        @include responsive.mq_down(sp) {
          min-height: auto;
          padding: 13px 15px 15px;
        }
      }

      &_plate {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        animation: slidePlateR 1s ease-in-out both;
        opacity: 0.7;
        background-color: var(--color_report_author_bg_user);
        will-change: opacity, width;

        &.is-imReport {
          background-color: var(--color_report_author_bg_im);
        }

        &.is-special {
          background-color: var(--color_special_author_bg);
        }

        &.is-news {
          background-color: var(--color_news_author_bg);
        }
      }

      &_type {
        display: inline-block;
        position: relative;
        animation: slideTextR 1s ease-in-out both;
        font-size: 18px;
        font-weight: 700;
        will-change: opacity, left;
        line-height: 35px;

        @include responsive.mq_down(sp) {
          font-size: 14px;
          line-height: 28px;
        }

        &::after {
          content: "";
          display: block;
          position: relative;
          height: 2px;
          background-color: #fff;
        }
      }

      &_title {
        position: relative;
        margin-top: 10px;
        margin-bottom: 15px;
        animation: slideTextR 1s ease-in-out both;
        font-size: 32px;
        font-weight: 700;
        line-height: 1.5;
        will-change: opacity, left;

        @include responsive.mq_down(sp) {
          margin-top: 8px;
          margin-bottom: 10px;
          font-size: 18px;
        }
      }

      &_body {
        position: relative;
        margin-top: 5px;
        animation: slideTextR 1s ease-in-out both;
        font-size: 14px;
        font-weight: 700;
        line-height: 30px;
        will-change: opacity, left;

        @include responsive.mq_down(sp) {
          font-size: 14px;
          line-height: 20px;
        }
      }
    }

    &_cover {
      position: absolute;
      top: 0;
      right: 20px;
      left: 20px;
      height: 100%;
      animation: fadeOutCoverR 1s ease-in-out both;
      opacity: 0;
      background-color: #000;
      will-change: opacity;

      @include responsive.mq_down(sp) {
        right: 0;
        left: 0;
      }
    }
  }
}

@keyframes slidePlate {
  0% {
    width: 0;
    opacity: 0;
  }

  100% {
    width: 100%;
    opacity: 0.7;
  }
}

@keyframes slidePlateR {
  0% {
    opacity: 0.7;
  }

  100% {
    opacity: 0;
  }
}

@keyframes slideText {
  0% {
    left: -100%;
    opacity: 0;
  }

  50% {
    opacity: 0;
  }

  100% {
    left: 0;
    opacity: 1;
  }
}

@keyframes slideTextR {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

@keyframes fadeOutCover {
  0% {
    opacity: 0.4;
  }

  100% {
    opacity: 0;
  }
}

@keyframes fadeOutCoverR {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 0.4;
  }
}
</style>
